import glObjectService from 'Infrastructure/Service/GLObjectService';
import { animationStore } from 'Domain/Mesh/State/AnimationStore';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';
import { getIdTooth } from 'Domain/Mesh/Query/ToothQueryService';
import * as THREE from 'three';

class GumDeformationManager {
    constructor() {
        this.requestStateByTooth = new Map();
        this.revision = 0;
    }

    _getToothRequestKey(toothData) {
        const rawToothId = getIdTooth(toothData);
        const toothId = Number.parseInt(rawToothId, 10);
        const safeToothId = Number.isNaN(toothId) ? -1 : toothId;
        return `${toothData.jawSide}:${safeToothId}`;
    }

    async deformGumForTooth(toothData, previousMatrix, movementType = 0) {
        if (!toothData || !toothData.refObj) {
            return;
        }

        const requestRevision = this.revision;

        const toothKey = this._getToothRequestKey(toothData);
        if (!this.requestStateByTooth.has(toothKey)) {
            this.requestStateByTooth.set(toothKey, {
                isProcessing: false,
                pendingRequest: null
            });
        }

        const requestState = this.requestStateByTooth.get(toothKey);

        if (requestState.isProcessing) {
            // Keep only the latest request while one is already in flight for this tooth
            requestState.pendingRequest = { toothData, previousMatrix, movementType, requestRevision };
            return;
        }

        try {
            requestState.isProcessing = true;

            await this._processDeformation(toothData, previousMatrix, movementType, requestRevision);

            // Process latest pending request for this tooth, if any
            while (requestState.pendingRequest) {
                const nextRequest = requestState.pendingRequest;
                requestState.pendingRequest = null;
                await this._processDeformation(nextRequest.toothData, nextRequest.previousMatrix, nextRequest.movementType, nextRequest.requestRevision);
            }

        } catch (error) {
            console.error('Failed to deform gum:', error);
        } finally {
            requestState.isProcessing = false;
            if (!requestState.pendingRequest) {
                this.requestStateByTooth.delete(toothKey);
            }
        }
    }

    async _processDeformation(toothData, previousMatrix, movementType = 0, requestRevision = this.revision) {
        const jawSide = toothData.jawSide; // 0 for maxi, 1 for mandi
        const positionKey = jawSide === 0 ? 'maxi' : 'mandi';
        
        // Get visible gum for this jaw
        const gengivasMap = animationStore.getGengivas();
        if (!gengivasMap || !gengivasMap[positionKey]) return;

        // Find the active/max step gengiva
        const maxKey = animationStore.getMaxGengivaKey(positionKey);
        const activeGumMesh = gengivasMap[positionKey][maxKey];

        if (!activeGumMesh || !activeGumMesh.geometry) return;

        // Extract original gum data (cache it to avoid sending already deformed gum)
        if (!activeGumMesh.userData.originalVertices) {
            const initialGumData = glObjectService._extractMeshData(activeGumMesh);
            activeGumMesh.userData.originalVertices = initialGumData.vertices;
            activeGumMesh.userData.originalIndices = initialGumData.indices;
        }

        const gumVertices = activeGumMesh.userData.originalVertices;
        const gumIndices = activeGumMesh.userData.originalIndices;

        // Extract tooth data (local space geometry)
        const toothMesh = toothData.refObj;
        const toothGeomData = glObjectService._extractMeshData(toothMesh);

        // --- MATRICES MAGIC to put tooth in GUM's local space ---
        // Ensure matrices are up to date
        toothData.pivot.parent.updateMatrixWorld();
        activeGumMesh.updateMatrixWorld();

        const parentWorld = toothData.pivot.parent.matrixWorld;
        const initPivot = toothData.mouvements[0];
        const refObjLocal = toothData.refObj.matrix;

        const initToothWorld = new THREE.Matrix4()
            .copy(parentWorld)
            .multiply(initPivot)
            .multiply(refObjLocal);

        const gumWorldInverse = activeGumMesh.matrixWorld.clone().invert();
        
        const toothToGumLocal = new THREE.Matrix4()
            .copy(gumWorldInverse)
            .multiply(initToothWorld);

        // Transform tooth vertices
        const rawToothVertices = toothGeomData.vertices;
        const transformedToothVertices = new Array(rawToothVertices.length);
        const vec = new THREE.Vector3();
        for (let i = 0; i < rawToothVertices.length; i += 3) {
            vec.set(rawToothVertices[i], rawToothVertices[i+1], rawToothVertices[i+2]);
            vec.applyMatrix4(toothToGumLocal);
            transformedToothVertices[i] = vec.x;
            transformedToothVertices[i+1] = vec.y;
            transformedToothVertices[i+2] = vec.z;
        }

        // Translation
        const initPivotWorld = new THREE.Matrix4().copy(parentWorld).multiply(initPivot);
        const currPivotWorld = new THREE.Matrix4().copy(parentWorld).multiply(toothData.pivot.matrix);
        
        const initPosWorld = new THREE.Vector3().setFromMatrixPosition(initPivotWorld);
        const currPosWorld = new THREE.Vector3().setFromMatrixPosition(currPivotWorld);
        
        const translationWorld = new THREE.Vector3().subVectors(currPosWorld, initPosWorld);
        const gumWorldInverseLinear = new THREE.Matrix3().setFromMatrix4(gumWorldInverse);
        const translationGumLocal = translationWorld.clone().applyMatrix3(gumWorldInverseLinear);
        
        const toothTranslation = [translationGumLocal.x, translationGumLocal.y, translationGumLocal.z];

        // Barycenter (same frame as transformedToothVertices)
        const barycenterLocal = toothData.baryCenter.position.clone();
        const barycenterGumLocal = barycenterLocal.applyMatrix4(toothToGumLocal);
        const barycenter = [barycenterGumLocal.x, barycenterGumLocal.y, barycenterGumLocal.z];

        // Debug sanity check: barycenter should be inside (or near) tooth bbox
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        for (let i = 0; i < transformedToothVertices.length; i += 3) {
            const x = transformedToothVertices[i];
            const y = transformedToothVertices[i + 1];
            const z = transformedToothVertices[i + 2];
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (z < minZ) minZ = z;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
            if (z > maxZ) maxZ = z;
        }
        const insideBBox =
            barycenter[0] >= minX && barycenter[0] <= maxX &&
            barycenter[1] >= minY && barycenter[1] <= maxY &&
            barycenter[2] >= minZ && barycenter[2] <= maxZ;

        console.log('=== GUM DEFORMATION MANAGER ===');
        console.log(`Deforming gum for jaw: ${positionKey}`);
        console.log('Translation world (x,y,z):', [translationWorld.x, translationWorld.y, translationWorld.z]);
        console.log('Calculated tooth translation (x,y,z):', toothTranslation);
        console.log('Tooth barycenter (x,y,z):', barycenter);
        console.log('Tooth bbox in gum local:', { minX, minY, minZ, maxX, maxY, maxZ, insideBBox });
        console.log('===============================');

        // Call API
        const caseId = String(threeEngine.getProjectId() ?? '');
        const toothId = parseInt(getIdTooth(toothData), 10);

        const apiResponse = await glObjectService.deformGum(
            gumVertices,
            gumIndices,
            transformedToothVertices,
            toothGeomData.indices,
            toothTranslation,
            barycenter,
            movementType,
            caseId,
            Number.isNaN(toothId) ? -1 : toothId
        );

        if (!apiResponse) return;
        if (requestRevision !== this.revision) return;

        // Update gum mesh keeping original indices
        glObjectService._updateMeshFromResponseKeepIndices(activeGumMesh, apiResponse, gumIndices);
    }

    _restoreMeshOriginalVertices(mesh) {
        if (!mesh?.geometry || !mesh?.userData?.originalVertices) return;

        const originalVertices = mesh.userData.originalVertices;
        const geometry = mesh.geometry;
        const positionAttr = geometry.attributes?.position;

        if (!positionAttr || positionAttr.array.length !== originalVertices.length) return;

        positionAttr.array.set(originalVertices);
        positionAttr.needsUpdate = true;
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();
    }

    resetAllDeformedGums() {
        this.revision += 1;
        this.requestStateByTooth.clear();

        const gengivasMap = animationStore.getGengivas();
        if (!gengivasMap) return;

        ['maxi', 'mandi'].forEach(positionKey => {
            Object.values(gengivasMap[positionKey] || {}).forEach(mesh => {
                this._restoreMeshOriginalVertices(mesh);
            });
        });
    }

}

const gumDeformationManager = new GumDeformationManager();
export default gumDeformationManager;
